import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, getStoredUser, type TenantListResult, type DemoSeedResult, type ArchiveRestoreResult } from '../api/client.js';
import { EmptyState, ErrorBanner, SkeletonCards, Spinner } from '../components/States.js';

function formatBytes(n: number): string {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** 配额用量条（≥80% 转警示色） */
function QuotaBar({ label, pct }: { label: string; pct: number }) {
  const tone = pct >= 80 ? 'bg-red-500' : pct >= 50 ? 'bg-amber-500' : 'bg-brand-500';
  return (
    <div title={`${label}：已用 ${pct}%`}>
      <div className="flex items-center justify-between text-[10px] text-slate-400">
        <span>{label}</span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="h-1 rounded-full bg-slate-200 overflow-hidden mt-0.5">
        <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

export default function AdminTenants() {
  const user = getStoredUser();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState<TenantListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState('');

  // 新建租户
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [seed, setSeed] = useState<DemoSeedResult | null>(null);

  // 归档恢复
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreTarget, setRestoreTarget] = useState('');
  const [restoreResult, setRestoreResult] = useState<ArchiveRestoreResult | null>(null);
  const [restoreSeq, setRestoreSeq] = useState(0); // 成功后重挂 file input，避免残留旧文件名

  const load = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      setData(await adminApi.tenants());
    } catch (e: any) {
      setError(e.message || '加载租户清单失败');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const createTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusyId('new');
    setError('');
    setNotice('');
    try {
      const r = await adminApi.createTenant(newId, newName || undefined);
      setNotice(r.created ? `已开通租户「${r.name}」` : `租户「${r.name}」已存在，已按新名称更新`);
      setNewId('');
      setNewName('');
      await load();
    } catch (err: any) {
      setError(err.message || '开通租户失败');
    } finally {
      setBusyId('');
    }
  };

  const downloadArchive = async (tenantId: string) => {
    setBusyId(tenantId);
    setError('');
    setNotice('');
    try {
      const r = await adminApi.archive(tenantId);
      setNotice(`已导出归档包 ${r.fileName}（${formatBytes(r.bytes)}）`);
    } catch (e: any) {
      setError(e.message || '归档导出失败');
    } finally {
      setBusyId('');
    }
  };

  const resetDemo = async () => {
    if (!window.confirm('将以幂等方式重建演示租户（default）的演示数据：清理 DEM- / DEMO- 标记的凭证、流水与影像后重新写入，确认继续？')) return;
    setBusyId('seed');
    setError('');
    setNotice('');
    setSeed(null);
    try {
      const r = await adminApi.resetDemo();
      setSeed(r);
      await load();
    } catch (e: any) {
      setError(e.message || '演示数据重置失败');
    } finally {
      setBusyId('');
    }
  };

  const restoreArchive = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restoreFile || !restoreTarget.trim()) return;
    const hint = `将以归档包「${restoreFile.name}」新建/填充租户「${restoreTarget.trim()}」：仅面向空租户，不会覆盖既有数据，确认继续？`;
    if (!window.confirm(hint)) return;
    setBusyId('restore');
    setError('');
    setNotice('');
    setRestoreResult(null);
    try {
      const r = await adminApi.restoreArchive(restoreTarget.trim(), restoreFile);
      setRestoreResult(r);
      const nJournal = r.inserted.journal_entries ?? 0;
      const nAtt = r.attachments;
      setNotice(`恢复完成：写入凭证 ${nJournal} 张${nAtt.total ? `，影像元数据 ${nAtt.total} 条（文件实体已就位 ${nAtt.filesPresent} 个）` : ''}${r.usersSkipped ? `，跳过用户 ${r.usersSkipped} 个（需另行建）` : ''}${r.skippedAccounts ? `，跳过同码科目 ${r.skippedAccounts} 个` : ''}`);
      setRestoreFile(null);
      setRestoreTarget('');
      setRestoreSeq((s) => s + 1);
      await load();
    } catch (err: any) {
      setError(err.message || '归档恢复失败');
    } finally {
      setBusyId('');
    }
  };

  if (!isAdmin) {
    return (
      <EmptyState
        icon="🔐"
        title="租户运维后台需以管理员身份登录"
        hint="该模块可跨租户查看数据体量与配额，因此未开放匿名 / 非管理员访问"
        action={<Link to="/login" className="btn btn-primary">前往登录（admin 一键填充）</Link>}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">租户运维后台</h2>
          <p className="text-sm text-slate-500 mt-0.5">租户清单与配额用量 · 数据归档导出/恢复 · 演示数据重置</p>
        </div>
        <button onClick={load} disabled={loading} className="btn btn-ghost btn-sm">↻ 刷新</button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {notice && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm flex items-center justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice('')} className="text-blue-400 hover:text-blue-600">×</button>
        </div>
      )}

      {/* 全局概览 + 开通租户 */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card card-pad lg:col-span-2">
          <h3 className="section-title mb-3">🏢 平台概览</h3>
          {loading && !data ? (
            <SkeletonCards count={1} className="space-y-3" />
          ) : data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="租户数" value={String(data.totals.tenants)} sub="含默认演示租户" />
              <Tile label="累计凭证" value={String(data.totals.journalEntries)} sub="全平台合计" />
              <Tile label="影像占用" value={formatBytes(data.totals.attachmentBytes)} sub="data/uploads" />
              <Tile label="单租户凭证上限" value={String(data.quotas.maxJournalEntries)} sub="软限制（仅告警）" />
            </div>
          )}
          {data && (
            <p className="text-[11px] text-slate-400 mt-3">
              配额为试点期软限制：凭证 {data.quotas.maxJournalEntries} 张 / 科目 {data.quotas.maxAccounts} 个 / 用户 {data.quotas.maxUsers} 人 / 附件 {formatBytes(data.quotas.maxAttachmentBytes)}；用量达 80% 转红提示，不阻断写入。
            </p>
          )}
        </div>

        <form onSubmit={createTenant} className="card card-pad">
          <h3 className="section-title mb-3">➕ 开通租户</h3>
          <label className="block text-xs text-slate-500 mb-1">租户 ID（字母/数字/-/_）</label>
          <input
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            placeholder="acme-co"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-2"
            required
          />
          <label className="block text-xs text-slate-500 mb-1">单位名称</label>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="杭州某某财税咨询有限公司"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3"
          />
          <button type="submit" disabled={busyId === 'new' || !newId.trim()} className="btn btn-primary btn-sm w-full">
            {busyId === 'new' ? '提交中…' : '开通 / 更新'}
          </button>
          <p className="text-[11px] text-slate-400 mt-2">同名 ID 再次提交只更新单位名称，不会重复建租户。</p>
        </form>
      </div>

      {/* 租户清单 */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-medium text-slate-700">租户清单</h3>
          <span className="text-xs text-slate-400">凭证 / 分录 / 流水 / 影像 / 预警</span>
        </div>
        {loading && !data ? (
          <div className="p-6 text-center"><Spinner /></div>
        ) : (data?.tenants.length || 0) === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400">暂无租户记录</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-2">租户</th>
                <th className="px-4 py-2 text-right">凭证（过账/草稿）</th>
                <th className="px-4 py-2 text-right">分录</th>
                <th className="px-4 py-2 text-right">用户 / 科目</th>
                <th className="px-4 py-2 text-right">微信流水</th>
                <th className="px-4 py-2 text-right">影像</th>
                <th className="px-4 py-2 text-right">未处理预警</th>
                <th className="px-4 py-2 w-56">配额用量</th>
                <th className="px-4 py-2 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data!.tenants.map((t) => {
                const topPct = Math.max(t.usage.pct.journalEntries, t.usage.pct.accounts, t.usage.pct.users, t.usage.pct.attachmentBytes);
                return (
                  <tr key={t.id} className="hover:bg-slate-50 align-top">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700">{t.name}</span>
                        {t.isDemo && <span className="badge bg-brand-50 text-brand-600">演示</span>}
                        {t.usage.nearLimit && <span className="badge bg-red-100 text-red-700" title={`${t.usage.nearest} 接近配额上限`}>接近上限</span>}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono">{t.id}</div>
                      <div className="text-[11px] text-slate-400">开通于 {t.createdAt?.slice(0, 10) || '—'}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">
                      {t.stats.journalEntries}
                      <span className="text-[11px] text-slate-400"> ({t.stats.postedEntries}/{t.stats.draftEntries})</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{t.stats.journalLines}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{t.stats.users} / {t.stats.accounts}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{t.stats.wechatTransactions}</td>
                    <td className="px-4 py-2.5 text-right font-mono">
                      {t.stats.attachments}
                      <span className="text-[11px] text-slate-400"> · {formatBytes(t.stats.attachmentBytes)}</span>
                    </td>
                    <td className={`px-4 py-2.5 text-right font-mono ${t.stats.unreadAlerts > 0 ? 'text-red-600 font-semibold' : 'text-slate-400'}`}>
                      {t.stats.unreadAlerts}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="space-y-1.5">
                        <QuotaBar label="凭证" pct={t.usage.pct.journalEntries} />
                        <QuotaBar label="附件" pct={t.usage.pct.attachmentBytes} />
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1">最高维度 {t.usage.nearest} {topPct}%</div>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => downloadArchive(t.id)}
                        disabled={busyId === t.id}
                        className="btn btn-accent btn-sm"
                        title="导出该租户全部业务数据（JSON 归档包，不含口令摘要与影像文件实体）"
                      >
                        {busyId === t.id ? '打包中…' : '⬇ 归档包'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 归档恢复 */}
      <form onSubmit={restoreArchive} className="card card-pad">
        <h3 className="section-title">📤 归档包恢复（迁移 / 重建）</h3>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          选择「⬇ 归档包」导出的 JSON 文件，整包恢复到新租户或空租户；已有数据的租户会被拒绝（409），不会覆盖。
          用户不随包恢复（归档不含口令），需在新租户另行建用户；影像只恢复元数据，文件实体需同步拷贝到 data/uploads/目标租户/ 目录。
        </p>
        <div className="grid md:grid-cols-[1fr_240px_auto] gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">归档文件（.json）</label>
            <input
              key={restoreSeq}
              type="file"
              accept=".json,application/json"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-600 hover:file:bg-slate-200"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">目标租户 ID（不存在则自动开通）</label>
            <input
              value={restoreTarget}
              onChange={(e) => setRestoreTarget(e.target.value)}
              placeholder="acme-co-restore"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
            />
          </div>
          <button type="submit" disabled={busyId === 'restore' || !restoreFile || !restoreTarget.trim()} className="btn btn-primary">
            {busyId === 'restore' ? '恢复中…' : '⇪ 恢复归档包'}
          </button>
        </div>
        {restoreResult && (
          <div className="mt-3 rounded-lg border border-green-200 bg-green-50/60 p-3 text-xs font-mono text-green-700 space-y-1">
            <div className="font-semibold">✔ {restoreResult.sourceTenantId} → {restoreResult.targetTenantId} 恢复完成</div>
            <div>{Object.entries(restoreResult.inserted).map(([t, n]) => `${t}=${n}`).join(' · ')}</div>
            <div>
              影像文件实体 {restoreResult.attachments.filesPresent}/{restoreResult.attachments.total}
              {restoreResult.usersSkipped > 0 && ` · 跳过用户 ${restoreResult.usersSkipped}`}
              {restoreResult.skippedAccounts > 0 && ` · 跳过同码科目 ${restoreResult.skippedAccounts}`}
            </div>
          </div>
        )}
      </form>

      {/* 演示数据重置 */}
      <div className="card card-pad">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="section-title">🌱 演示数据重置</h3>
            <p className="text-sm text-slate-500 mt-1">
              供试点评审现场救场：按脚本口径重建 default 租户的科目、业务类型、近三个月演示凭证、三档置信度流水与示例影像（幂等，重复执行结果一致）。
            </p>
          </div>
          <button onClick={resetDemo} disabled={busyId === 'seed'} className="btn btn-warn">
            {busyId === 'seed' ? '重置中…' : '↺ 重置演示租户数据'}
          </button>
        </div>
        {seed && (
          <div className={`mt-3 rounded-lg border p-3 text-xs font-mono ${seed.ok ? 'border-green-200 bg-green-50/60 text-green-700' : 'border-red-200 bg-red-50/60 text-red-700'}`}>
            <div className="font-semibold mb-1">
              {seed.ok ? `✔ 演示数据写入完成（${(seed.durationMs / 1000).toFixed(1)}s）` : `✘ 脚本异常退出（exit ${seed.exitCode}）`}
            </div>
            {seed.output.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold font-mono text-slate-800 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}
